import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * A single breadcrumb navigation item.
 *
 * Renders as a clickable anchor link when `href` is set and `active` is false.
 * Renders as a non-interactive `<span>` with `aria-current="page"` when
 * `active` is true, or a plain `<span>` when `href` is empty and the item is
 * not the current page. Intended for use as a direct child of `asc-breadcrumbs`;
 * separators are the parent widget's responsibility.
 *
 * Icon-only items (e.g. a home breadcrumb) must include an `aria-label` on the
 * host element for screen readers. The icon itself should carry `aria-hidden="true"`.
 *
 * @element asc-breadcrumb-item
 *
 * @slot - The visible content of the breadcrumb item — plain text, a `<span>`,
 *   an `<svg>`, or an `<asc-icon>`. Required.
 *
 * @csspart base - The root element: `<a>` when linkable, `<span>` when active
 *   or href is absent.
 *
 * @cssprop --asc-breadcrumb-item-color - Text/link color in the default state. Default: `var(--asc-text-quaternary)`
 * @cssprop --asc-breadcrumb-item-color-hover - Text color on link hover. Default: `var(--asc-text-brand-tertiary)`
 * @cssprop --asc-breadcrumb-item-color-active - Text color when active (current page). Default: `var(--asc-text-quaternary)`
 * @cssprop --asc-breadcrumb-item-font-size - Font size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-breadcrumb-item-font-weight - Font weight. Default: `var(--asc-font-weight-semibold)`
 */
@customElement('asc-breadcrumb-item')
export class AscBreadcrumbItem extends LitElement {
  static override styles = css`
    :host {
      display: inline-flex;
      align-items: center;
    }

    .base {
      display: inline-flex;
      align-items: center;
      gap: var(--asc-spacing-xxs);
      padding: var(--asc-spacing-xxs);
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-breadcrumb-item-font-size, var(--asc-font-size-text-sm));
      font-weight: var(--asc-breadcrumb-item-font-weight, var(--asc-font-weight-semibold));
      line-height: var(--asc-line-height-text-sm);
      color: var(--asc-breadcrumb-item-color, var(--asc-text-quaternary));
      text-decoration: none;
      transition: color var(--asc-transition-fast);
    }

    a.base {
      cursor: pointer;
      border-radius: var(--asc-radius-xs);
    }

    a.base:hover {
      color: var(--asc-breadcrumb-item-color-hover, var(--asc-text-brand-tertiary));
    }

    a.base:focus-visible {
      outline: none;
      background-color: var(--asc-bg-primary);
      box-shadow: 0 0 0 2px var(--asc-bg-primary), 0 0 0 4px var(--asc-focus-ring);
    }

    :host([active]) .base {
      color: var(--asc-breadcrumb-item-color-active, var(--asc-text-quaternary));
    }
  `;

  /**
   * The URL the breadcrumb navigates to. When set (and `active` is false), renders
   * an `<a>` inside Shadow DOM. Note: the link lives in Shadow DOM, which is
   * acceptable for Googlebot but may reduce crawlability for non-JS crawlers.
   * See `.github/instructions/seo.instructions.md` for details.
   */
  @property({ type: String }) href = '';

  /**
   * Marks this item as the current page. Renders as a non-interactive `<span>`
   * with `aria-current="page"`, regardless of `href`. Reflected for
   * `:host([active])` CSS selectors. The parent `asc-breadcrumbs` widget sets
   * this automatically on the last item.
   */
  @property({ type: Boolean, reflect: true }) active = false;

  /**
   * Optional accessible name for icon-only breadcrumb items.
   * Forwarded to the internal base element (`<a>` or `<span>`).
   */
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;

  override render() {
    if (!this.active && this.href) {
      return html`
        <a
          part="base"
          class="base"
          href="${this.href}"
          aria-label=${this.ariaLabel || nothing}
        >
          <slot></slot>
        </a>
      `;
    }
    return html`
      <span
        part="base"
        class="base"
        aria-label=${this.ariaLabel || nothing}
        aria-current=${this.active ? 'page' : nothing}
      >
        <slot></slot>
      </span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-breadcrumb-item': AscBreadcrumbItem;
  }
}
